import React from 'react'
import { styled } from '@mui/material/styles'
import StrapiImage from '@templates/strapi-cms/content-types/Image'
import { useTest } from '@hooks/useTest'
import BaseSwiper from '../../../../@rtg2022/components/organisms/BaseSwiper/BaseSwiper'
import SaleFlag from '../../../../@rtg2022/components/atoms/SaleFlag/SaleFlag'
import SizesLabel from '../components/SizesLabel'
import { useBaseCollectionTileContext } from '../BaseCollectionTileContext'
import { determineIsOnSale } from '../helpers'

const ImgWrapper = styled('div')(() => ({
  aspectRatio: '3 / 2',
  position: 'relative',
  height: '100%',
  width: '100%',
}))

const SaleFlagWrapper = styled('div')(({ theme }) => ({
  '& > div': {
    borderRadius: '4px',
    padding: '4px 8px',
    top: '4px',
    left: '4px',
    [theme.breakpoints.up('sm')]: {
      top: '8px',
      left: '8px',
    },
  },
  '& > div > p': {
    fontSize: '14px',
    lineHeight: 'normal',
    [theme.breakpoints.up('sm')]: {
      fontSize: '18px',
    },
  },
}))

const ImageGallery = () => {
  const { collection, priceZone, activeIndexTracker, slideToIndex } = useBaseCollectionTileContext()
  const { variations, imageLabels } = collection ?? {}
  const { generateTestId } = useTest()

  if (!variations) return null

  const isOnSale = determineIsOnSale(collection, priceZone)

  const slideImages = Object.entries(variations)
    .map(([key, value]) => {
      // For now, we only want to show room images
      const { itemImages, roomImages } = value ?? {}
      if (!roomImages) return []
      return roomImages
    })
    .flat()

  const slides = slideImages.map((image, index) => (
    <ImgWrapper key={image}>
      <StrapiImage
        data={{
          desktop: {
            url: image,
          },
          alt: image,
        }}
        noPaddingBottom
      />
    </ImgWrapper>
  ))

  return (
    <div
      style={{ position: 'relative' }}
      role="region"
      aria-label="Collection image gallery"
      data-testid={generateTestId('collection-tile', 'image-gallery')}
    >
      <BaseSwiper
        desktopSlides={slides}
        mobileSlides={slides}
        slidesPerViewDesktop={1}
        slidesPerViewMobile={1}
        spaceBetween={16}
        spaceBetweenMobile={16}
        hideScrollbar
        navigationVariant="imageCarousel"
        enableMobileBullets
        activeIndexTracker={activeIndexTracker}
        slideToIndex={slideToIndex}
        loop
      />
      {isOnSale && (
        <SaleFlagWrapper>
          <SaleFlag label="sale" />
        </SaleFlagWrapper>
      )}
      <SizesLabel imageLabels={imageLabels} />
    </div>
  )
}

export default ImageGallery
